<template>
  <div class="wa-page">
    <aside class="wa-list">
      <div class="wa-list__header">
        <h2>WhatsApp</h2>
        <select v-model="stateFilter" aria-label="Dialogstatus">
          <option value="open">Offene Dialoge</option>
          <option value="completed">Abgeschlossene Dialoge</option>
        </select>
        <input v-model="search" placeholder="Name oder Telefon">
      </div>
      <button
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        class="wa-contact"
        :class="{'wa-contact--active': selectedId === conversation.id}"
        @click="selectConversation(conversation.id)"
      >
        <strong>{{ conversation.profile_name || conversation.wa_id }}</strong>
        <span>{{ productTitle(conversation.product_code) }}</span>
        <small>{{ stateTitle(conversation.state) }}</small>
      </button>
      <p v-if="!loading && !filteredConversations.length" class="wa-empty">
        Keine Dialoge gefunden.
      </p>
    </aside>

    <section v-if="selected" class="wa-chat">
      <header class="wa-chat__header">
        <div>
          <h3>{{ selected.profile_name || selected.wa_id }}</h3>
          <span>+{{ selected.wa_id.replace(/^\+/, '') }}</span>
          <div v-if="selected.token_usage" class="wa-token-usage">
            KI Tokens:
            <strong>{{ formatNumber(selected.token_usage.total_tokens) }}</strong>
            <small>
              Input {{ formatNumber(selected.token_usage.input_tokens) }} /
              Output {{ formatNumber(selected.token_usage.output_tokens) }}
            </small>
          </div>
        </div>
        <label class="wa-toggle">
          <input
            type="checkbox"
            :checked="selected.bot_enabled"
            @change="toggleBot($event.target.checked)"
          >
          Bot aktiv
        </label>
        <button
          v-if="isUnread"
          type="button"
          class="wa-secondary"
          :disabled="busy"
          @click="markConversationRead"
        >
          Als gelesen markieren
        </button>
      </header>

      <div ref="messages" class="wa-messages">
        <div
          v-for="message in selected.messages"
          :key="message.id"
          class="wa-message"
          :class="`wa-message--${message.direction}`"
        >
          <div class="wa-message__bubble">
            <div v-if="message.text" class="wa-message__text">{{ message.text }}</div>
            <button
              v-if="message.media_id"
              class="wa-file"
              @click="downloadMedia(message.id)"
            >
              📎 {{ message.filename || message.mime_type || 'Dokument' }}
            </button>
            <small>{{ formatDate(message.created_at) }} · {{ message.status || '' }}</small>
          </div>
        </div>
      </div>
      <div v-if="pendingAiSuggestion" class="wa-ai-draft">
        <div>
          <strong>KI-Antwortvorschlag</strong>
          <small>{{ pendingAiSuggestion.payload.ai.reason }}</small>
        </div>
        <textarea
          v-model="aiDraft"
          rows="4"
          placeholder="Antwort pruefen oder selbst formulieren..."
        />
        <div class="wa-ai-draft__actions">
          <span>Wird erst nach Ihrer Bestaetigung gesendet.</span>
          <button
            type="button"
            :disabled="busy || !aiDraft.trim()"
            @click="sendAiDraft"
          >
            Ueber WhatsApp senden
          </button>
        </div>
      </div>


      <form class="wa-reply" @submit.prevent="sendReply">
        <textarea v-model="reply" placeholder="Nachricht schreiben…" rows="2" />
        <button :disabled="busy || !reply.trim()">Senden</button>
      </form>
    </section>

    <aside v-if="selected" class="wa-details">
      <div class="wa-details__title">
        <div>
          <h3>Bestelldaten</h3>
          <span class="wa-status">{{ stateTitle(selected.state) }}</span>
        </div>
        <strong>{{ productTitle(selected.product_code) }}</strong>
      </div>

      <div class="wa-fields">
        <label v-for="key in editableKeys" :key="key">
          <span>{{ fieldTitle(key) }}</span>
          <select
            v-if="typeof editableDraft[key] === 'boolean'"
            v-model="editableDraft[key]"
            @change="editing = true"
          >
            <option :value="true">Ja</option>
            <option :value="false">Nein</option>
          </select>
          <input v-else v-model="editableDraft[key]" @input="editing = true">
        </label>
      </div>
      <button class="wa-secondary" :disabled="busy" @click="saveDraft">
        Änderungen speichern
      </button>

      <div v-if="documentGroups.length" class="wa-documents">
        <h4>Dokumente</h4>
        <div v-for="group in documentGroups" :key="group.key" class="wa-document-group">
          <strong>{{ documentTitle(group.key) }}</strong>
          <button
            v-for="file in group.files"
            :key="file.message_id"
            @click="downloadMedia(file.message_id)"
          >
            📎 {{ file.filename || file.mime_type || 'Datei' }}
          </button>
        </div>
      </div>

      <div v-if="selected.applications && selected.applications.length" class="wa-history">
        <h4>Bestellhistorie</h4>
        <router-link
          v-for="application in selected.applications"
          :key="application.id"
          :to="applicationOrderLink(application)"
        >
          {{ productTitle(application.product_code) }} /
          #{{ application.kzevb_order_id || application.kkv_order_id }} /
          {{ formatDate(application.created_at) }}
        </router-link>
      </div>

      <div class="wa-actions">
        <router-link v-if="orderLink" :to="orderLink">Bestellung öffnen</router-link>
        <button
          v-else
          class="wa-primary"
          :disabled="busy || !selected.product_code"
          @click="createOrder"
        >
          Bestellung erstellen
        </button>
        <button
          v-if="orderLink"
          class="wa-primary"
          :disabled="busy"
          @click="sendPaymentLink"
        >
          Stripe-Link senden
        </button>
        <button
          v-if="selected.state !== 'completed'"
          class="wa-secondary"
          :disabled="busy"
          @click="completeConversation"
        >
          Dialog abschließen
        </button>
      </div>
      <p v-if="notice" class="wa-notice">{{ notice }}</p>
      <p v-if="error" class="wa-error">{{ error }}</p>
    </aside>

    <div v-if="!selected" class="wa-placeholder">
      <p v-if="loading">Dialoge werden geladen…</p>
      <p v-else>Wählen Sie links einen WhatsApp-Dialog aus.</p>
    </div>
  </div>
</template>

<script>
import authRequired from '@/mixins/auth_required'

const FIELD_TITLES = {
  customer_type: 'Kundentyp', contact_email: 'E-Mail', contact_phone: 'Telefon',
  delivery_name: 'Vorname', delivery_surname: 'Nachname',
  delivery_company_name: 'Firma', delivery_street: 'Straße',
  delivery_house: 'Hausnummer', delivery_postcode: 'PLZ',
  delivery_state: 'Ort', delivery_country: 'Land',
  person_name: 'Antragsteller Vorname', person_surname: 'Antragsteller Nachname',
  person_company_name: 'Antragsteller Firma', person_street: 'Antragsteller Straße',
  person_house: 'Antragsteller Hausnummer', person_postcode: 'Antragsteller PLZ',
  person_state: 'Antragsteller Ort', person_country: 'Antragsteller Land',
  payment_name: 'Rechnung Name', payment_surname: 'Rechnung Nachname',
  payment_company_name: 'Rechnung Firma', payment_street: 'Rechnung Straße',
  payment_house: 'Rechnung Hausnummer', payment_postcode: 'Rechnung PLZ',
  payment_state: 'Rechnung Ort', payment_country: 'Rechnung Land',
  start_date: 'Wunschdatum', vin_number: 'FIN/VIN',
  valid_tuv_or_hu_available: 'HU/TÜV vorhanden',
  vehicle_deregistered: 'Fahrzeug abgemeldet', tax_number: 'USt-IdNr.',
  payment_same_data_flag: 'Rechnungsadresse identisch', additional_info: 'Hinweise'
}

export default {
  name: 'whatsapp-dialogs',
  mixins: [authRequired],
  data: () => ({
    conversations: [], selected: null, selectedId: null,
    editableDraft: {}, search: '', stateFilter: 'completed', reply: '', loading: true, busy: false,
    error: '', notice: '', interval: null, editing: false, refreshing: false,
    aiDraft: '', aiMessageId: null
  }),
  computed: {
    filteredConversations () {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.conversations
      return this.conversations.filter(item =>
        `${item.profile_name} ${item.wa_id} ${item.product_code}`.toLowerCase().includes(query)
      )
    },
    editableKeys () {
      return Object.keys(this.editableDraft).sort((a, b) =>
        (FIELD_TITLES[a] || a).localeCompare(FIELD_TITLES[b] || b)
      )
    },
    documentGroups () {
      const documents = (this.selected.draft_data || {}).documents || {}
      return Object.keys(documents)
        .map(key => ({ key, files: documents[key] || [] }))
        .filter(group => group.files.length)
    },
    orderLink () {
      if (this.selected.kzevb_order_id) return `/kzevb/orders/${this.selected.kzevb_order_id}`
      if (this.selected.kkv_order_id) return `/kkv/orders/${this.selected.kkv_order_id}`
      return null
    },
    pendingAiSuggestion () {
      if (!this.selected || !this.selected.messages) return null
      return [...this.selected.messages].reverse().find(message =>
        message.direction === 'inbound' && message.payload &&
        message.payload.ai && message.payload.ai.status === 'pending'
      ) || null
    },
    isUnread () {
      if (!this.selected || !this.selected.messages) return false
      const lastReadId = Number(this.selected.last_read_message_id || 0)
      return this.selected.messages.some(message =>
        message.direction === 'inbound' && Number(message.id) > lastReadId
      )
    }
  },
  watch: {
    async stateFilter () {
      this.selected = null
      this.selectedId = null
      await this.loadConversations()
    }
  },
  async mounted () {
    const queryConversationId = Number(this.$route.query.conversation_id)
    if (queryConversationId) {
      this.selectedId = queryConversationId
    }
    await this.loadConversations()
    this.interval = window.setInterval(this.refresh, 15000)
  },
  beforeDestroy () {
    window.clearInterval(this.interval)
  },
  methods: {
    async loadConversations () {
      try {
        this.conversations = await this.$services.whatsapp.getConversations(200, 0, this.stateFilter)
        if (!this.selectedId && this.conversations.length) {
          await this.selectConversation(this.conversations[0].id)
        }
      } catch (error) {
        this.setError(error)
      } finally {
        this.loading = false
      }
    },
    async refresh () {
      if (this.refreshing || this.editing || this.busy) return
      this.refreshing = true
      try {
        await this.loadConversations()
        if (this.selectedId && !this.editing && !this.busy) {
          await this.loadSelected(false)
        }
      } finally {
        this.refreshing = false
      }
    },
    async selectConversation (id) {
      this.selectedId = id
      this.editing = false
      await this.loadSelected(true)
    },
    async loadSelected (replaceDraft) {
      const conversationId = this.selectedId
      try {
        const selected = await this.$services.whatsapp.getConversation(conversationId)
        if (conversationId !== this.selectedId) return
        this.selected = selected
        if (replaceDraft) this.makeEditableDraft()
        this.syncAiSuggestion()
        this.$nextTick(this.scrollMessages)
      } catch (error) {
        this.setError(error)
      }
    },
    makeEditableDraft () {
      const draft = this.selected.draft_data || {}
      this.editableDraft = Object.keys(draft).reduce((result, key) => {
        if (!key.startsWith('_') && key !== 'documents' && key !== 'confirm' &&
          key !== 'phone_confirm' && key !== 'same_delivery_address' &&
          key !== 'same_payment_address') result[key] = draft[key]
        return result
      }, {})
    },
    async saveDraft () {
      await this.run(async () => {
        await this.$services.whatsapp.updateDraft(this.selectedId, this.editableDraft)
        this.editing = false
        await this.loadSelected(true)
        this.notice = 'Daten gespeichert.'
      })
    },
    syncAiSuggestion () {
      const suggestion = this.pendingAiSuggestion
      if (!suggestion) {
        this.aiMessageId = null
        this.aiDraft = ''
        return
      }
      if (this.aiMessageId !== suggestion.id) {
        this.aiMessageId = suggestion.id
        this.aiDraft = suggestion.payload.ai.suggested_reply || ''
      }
    },
    async sendAiDraft () {
      const text = this.aiDraft.trim()
      if (!text || !this.pendingAiSuggestion) return
      await this.run(async () => {
        await this.$services.whatsapp.resolveAiSuggestion(
          this.selectedId, this.pendingAiSuggestion.id, text
        )
        this.aiMessageId = null
        this.aiDraft = ''
        this.notice = 'KI-Entwurf wurde ?ber WhatsApp gesendet.'
        window.dispatchEvent(new Event('whatsapp-pending-updated'))
        await this.loadSelected(false)
      })
    },
    async sendReply () {
      const text = this.reply.trim()
      if (!text) return
      await this.run(async () => {
        if (this.pendingAiSuggestion) {
          await this.$services.whatsapp.resolveAiSuggestion(
            this.selectedId, this.pendingAiSuggestion.id, text
          )
          window.dispatchEvent(new Event('whatsapp-pending-updated'))
        } else {
          await this.$services.whatsapp.sendMessage(this.selectedId, text, true)
        }
        this.reply = ''
        await this.loadSelected(false)
      })
    },
    async toggleBot (enabled) {
      await this.run(async () => {
        await this.$services.whatsapp.setBotEnabled(this.selectedId, enabled)
        await this.loadSelected(false)
      })
    },
    async markConversationRead () {
      await this.run(async () => {
        await this.$services.whatsapp.markConversationRead(this.selectedId)
        window.dispatchEvent(new Event('whatsapp-pending-updated'))
        await this.loadSelected(false)
      })
    },
    async completeConversation () {
      if (!window.confirm('WhatsApp-Dialog wirklich abschließen? Der Auftrag wird nicht geändert.')) return
      await this.run(async () => {
        await this.$services.whatsapp.completeConversation(this.selectedId)
        if (this.stateFilter === 'open') {
          this.selected = null
          this.selectedId = null
        } else {
          await this.loadSelected(false)
        }
        await this.loadConversations()
      })
    },
    async createOrder () {
      await this.run(async () => {
        await this.$services.whatsapp.updateDraft(this.selectedId, this.editableDraft)
        const result = await this.$services.whatsapp.createOrder(this.selectedId)
        this.notice = `Bestellung ${result.order_id} wurde erstellt.`
        await this.loadSelected(true)
        await this.loadConversations()
      })
    },
    async sendPaymentLink () {
      if (!window.confirm('Stripe-Zahlungslink jetzt per WhatsApp senden?')) return
      await this.run(async () => {
        await this.$services.whatsapp.createPaymentLink(this.selectedId, 'sparkassen', 'de')
        this.notice = 'Zahlungslink wurde gesendet.'
        await this.loadSelected(false)
      })
    },
    async downloadMedia (messageId) {
      await this.run(async () => {
        const media = await this.$services.whatsapp.getMedia(messageId)
        const bytes = Uint8Array.from(window.atob(media.content), char => char.charCodeAt(0))
        const url = window.URL.createObjectURL(new Blob([bytes], { type: media.mime_type }))
        const link = document.createElement('a')
        link.href = url
        link.download = media.filename
        link.click()
        window.URL.revokeObjectURL(url)
      })
    },
    async run (callback) {
      this.busy = true
      this.error = ''
      this.notice = ''
      try { await callback() } catch (error) { this.setError(error) } finally { this.busy = false }
    },
    setError (error) {
      this.error = error && error.message ? error.message : 'Aktion fehlgeschlagen.'
    },
    scrollMessages () {
      if (this.$refs.messages) this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
    },
    productTitle (code) {
      return ({ kzevb_de: 'eVB Deutschland', kzevb_eu: 'eVB Europa', kkv_de: 'Komplettpaket DE', kkv_eu: 'Komplettpaket EU' })[code] || 'Produkt noch offen'
    },
    stateTitle (state) {
      return ({ new: 'Neu', collecting_data: 'Datenerfassung', ready_for_review: 'Zu prüfen', human_takeover: 'Mitarbeiter', order_created: 'Bestellung erstellt', payment_link_sent: 'Zahlungslink gesendet', completed: 'Abgeschlossen' })[state] || state
    },
    fieldTitle (key) { return FIELD_TITLES[key] || key.split('_').join(' ') },
    applicationOrderLink (application) {
      if (application.kzevb_order_id) return `/kzevb/orders/${application.kzevb_order_id}`
      return `/kkv/orders/${application.kkv_order_id}`
    },
    documentTitle (key) {
      return ({ ausweis: 'Ausweis / Reisepass', vehicle_documents: 'Fahrzeugdokumente', tuv: 'HU / TÜV', vollmacht: 'Vollmacht' })[key] || key
    },
    formatDate (value) { return value ? new Date(value).toLocaleString('de-DE') : '' },
    formatNumber (value) { return Number(value || 0).toLocaleString('de-DE') }
  }
}
</script>

<style lang="stylus" scoped>
.wa-page
  display grid
  grid-template-columns 270px minmax(390px, 1fr) 350px
  height calc(100vh - 92px)
  background #f4f6f8
  color #263238
.wa-list, .wa-details
  overflow-y auto
  background white
.wa-list
  border-right 1px solid #dfe4e7
  &__header
    padding 14px
    border-bottom 1px solid #e4e8ea
    h2
      margin 0 0 10px
    input
      box-sizing border-box
      width 100%
.wa-contact
  display flex
  flex-direction column
  width 100%
  padding 12px 14px
  border 0
  border-bottom 1px solid #edf0f2
  background white
  text-align left
  cursor pointer
  span, small
    margin-top 4px
  small
    color #718089
  &--active
    background #e9f7ef
    border-left 4px solid #25d366
.wa-chat
  display flex
  min-width 0
  flex-direction column
  background #efeae2
  &__header
    display flex
    justify-content space-between
    align-items center
    padding 10px 16px
    background white
    border-bottom 1px solid #dfe4e7
    h3
      margin 0
.wa-token-usage
  display flex
  gap 6px
  align-items baseline
  margin-top 4px
  color #617078
  small
    color #77858c
.wa-toggle
  display flex
  gap 6px
  align-items center
.wa-messages
  flex 1
  overflow-y auto
  padding 18px
.wa-message
  display flex
  margin-bottom 9px
  &--outbound
    justify-content flex-end
    .wa-message__bubble
      background #d9fdd3
  &--system
    justify-content center
  &__bubble
    max-width 75%
    padding 9px 11px
    border-radius 8px
    background white
    box-shadow 0 1px 1px rgba(0,0,0,.08)
    small
      display block
      margin-top 5px
      color #77858c
      text-align right
  &__text
    white-space pre-wrap
.wa-file
  display block
  margin-top 7px
  border 0
  background transparent
  color #167b55
  cursor pointer
.wa-ai-draft
  padding 12px
  border-top 3px solid #7c3aed
  background #f5f3ff
  > div:first-child
    display flex
    justify-content space-between
    gap 12px
    small
      color #6b7280
  textarea
    box-sizing border-box
    width 100%
    margin-top 8px
    resize vertical
  &__actions
    display flex
    justify-content space-between
    align-items center
    gap 10px
    margin-top 8px
    span
      color #6b7280
      font-size 12px
    button
      padding 8px 12px
      border 0
      background #7c3aed
      color white
      cursor pointer
.wa-reply
  display flex
  gap 8px
  padding 10px
  background #f0f2f5
  textarea
    flex 1
    resize none
  button
    padding 0 20px
.wa-details
  border-left 1px solid #dfe4e7
  padding 14px
  &__title
    display flex
    justify-content space-between
    gap 10px
    h3
      margin 0 0 4px
.wa-status
  color #69777e
.wa-fields
  display grid
  grid-template-columns 1fr 1fr
  gap 9px
  margin 16px 0 10px
  label
    display flex
    flex-direction column
    gap 3px
    span
      font-size 12px
      color #637179
    input, select
      min-width 0
      box-sizing border-box
      width 100%
.wa-documents
  margin-top 20px
.wa-document-group
  display flex
  flex-direction column
  gap 5px
  padding 8px 0
  border-bottom 1px solid #e5e9eb
  button
    text-align left
.wa-actions
  display flex
  flex-direction column
  gap 8px
  margin-top 18px
.wa-primary, .wa-secondary
  padding 9px 12px
  cursor pointer
.wa-primary
  border 0
  background #167b55
  color white
.wa-secondary
  width 100%
.wa-error
.wa-history
  display flex
  flex-direction column
  gap 6px
  margin-top 18px
  h4
    margin-bottom 2px
  a
    color #167b55
  color #b42318
.wa-notice
  color #167b55
.wa-placeholder
  grid-column 2 / 4
  display flex
  align-items center
  justify-content center
.wa-empty
  padding 14px
@media (max-width: 1150px)
  .wa-page
    grid-template-columns 230px 1fr
  .wa-details
    position fixed
    z-index 4
    top 100px
    right 0
    bottom 0
    width 340px
    box-shadow -3px 0 10px rgba(0,0,0,.15)
</style>
